import {useState, useEffect, useMemo, useRef} from 'react';
import {jobActionHandlers} from 'hooks';
import {useDashboardContext} from 'contexts/DashboardContext';
import {useAppContext} from 'contexts/ApplicationContext';
import {isDeviceSmall} from 'shared/helpers/DeviceSize';
import {useConfirmationDialog} from 'shared/components/ConfirmationDialog/ConfirmationDialog';
import {parseHtmlString} from 'shared/helpers/HTMLParser';
import {flatten} from 'lodash';
import {DateTime} from '@cabinetsbycomputer/datetime';
import {
    invalidateJobs,
    Jobs,
    useGetJobsQuery,
} from 'components/customer/Job/store/jobApi';
import {DashboardJob} from 'components/customer/Job/entity/DashboardJob';
import {useAppDispatch} from 'store/customer';

export const useTimeOfDay = (): string => {
    const hour: number = parseInt(DateTime.nowLocal().format('HH').toString());

    if (hour < 12) {
        return 'morning';
    } else if (hour >= 12 && hour < 18) {
        return 'afternoon';
    } else {
        return 'evening';
    }
};

type UpdatesPopupType = {
    showUpdatesInfo: () => void;
    showContactsInfo: () => void;
    dialog: JSX.Element;
    messages: string | JSX.Element | JSX.Element[];
    contact: string | JSX.Element | JSX.Element[];
};

export const useUpdatesPopup = (): UpdatesPopupType => {
    const {userProfile} = useAppContext();
    const {dialog, showDialog} = useConfirmationDialog('', '', true);

    const [messages, contact] = useMemo(() => {
        const messages = parseHtmlString(userProfile.manufacturerNews);
        const contact = parseHtmlString(userProfile.manufacturerContactInfo);

        return [messages, contact];
    }, [userProfile]);

    const showUpdatesInfo = () => {
        showDialog({
            title: 'News and Updates',
            message:
                userProfile.manufacturerNews ||
                'No current News & Updates to display',
            hideYesButton: true,
            hideNoButton: true,
            keyboard: false,
            buttons: [
                {
                    name: 'Ok',
                    show: true,
                },
            ],
        });
    };

    const showContactsInfo = () => {
        showDialog({
            title: 'Contact Details',
            message: userProfile.manufacturerContactInfo,
            hideYesButton: true,
            hideNoButton: true,
            keyboard: false,
            buttons: [
                {
                    name: 'Ok',
                    show: true,
                },
            ],
        });
    };

    return {
        messages,
        contact,
        showUpdatesInfo,
        showContactsInfo,
        dialog,
    };
};

type DashboardTableConfigType = {
    loader: boolean;
    userName: string;
    jobs: Jobs;
    isNewCustomer: boolean;
    dialog: JSX.Element;
};

export const useDashboardTableConfig = (): DashboardTableConfigType => {
    const dispatch = useAppDispatch();
    const {setLastJob, setJobInfo} = useDashboardContext();
    const isSmallDevice = isDeviceSmall();
    const {userProfile, refreshDashboard, successDialog, setSuccessDialog} =
        useAppContext();

    const [isNewCustomer, setIsNewCustomer] = useState<boolean>(true);
    const {navigate, showDialog, dialog} = jobActionHandlers();
    const [poll, setPoll] = useState(false);

    const limit = isSmallDevice ? 5 : 10;
    const {
        data: jobs,
        isUninitialized,
        isLoading,
    } = useGetJobsQuery(
        {limit},
        {
            pollingInterval: poll ? 60 * 1000 : 0,
            skipPollingIfUnfocused: true,
        }
    );

    const localRefreshDashboardStatus = useRef(refreshDashboard.status);
    useEffect(() => {
        if (localRefreshDashboardStatus.current != refreshDashboard.status) {
            localRefreshDashboardStatus.current = refreshDashboard.status;
            dispatch(invalidateJobs());
        }
    }, [refreshDashboard]);

    useEffect(() => {
        if (successDialog?.length) {
            if (successDialog[0]?.type === 'copy') {
                showDialog({
                    title: `We're done!`,
                    message: successDialog[0].message,
                    keyboard: false,
                    hideYesButton: true,
                    hideNoButton: true,
                    buttons: [
                        {
                            show: true,
                            controlledHideDialog: false,
                            name: 'Close',
                            variant: 'secondary',
                        },
                    ],
                });
            } else {
                showDialog({
                    title: `We're done!`,
                    message: successDialog[0].message,
                    keyboard: false,
                    setShow: true,
                    yes: () => {
                        navigate(successDialog[0].link);
                    },
                });
            }

            return () => {
                setSuccessDialog([]);
            };
        }
    }, [successDialog]);

    useEffect(() => {
        if (jobs) {
            setJobInfo({
                'pending-confirmation-jobs':
                    jobs.pendingConfirmationJobs.length,
                'production-jobs': jobs.productionJobs.length,
                'quoted-jobs': jobs.quotedJobs.length,
                'pending-approval-jobs': jobs.pendingApprovalJobs.length,
                'completed-jobs': jobs.completedJobs.length,
                'pending-payment-jobs': jobs.pendingPaymentJobs.length,
            });

            const allJobs = flatten(Object.values(jobs)) as DashboardJob[];

            if (allJobs.length) {
                const latestJob = allJobs.reduce((a, b) => {
                    // Parse dates and check if they are valid
                    const dateA =
                        a.dateUpdatedDateTime && a.dateUpdatedDateTime.date
                            ? DateTime.parse(a.dateUpdatedDateTime.date)
                            : null;
                    const dateB =
                        b.dateUpdatedDateTime && b.dateUpdatedDateTime.date
                            ? DateTime.parse(b.dateUpdatedDateTime.date)
                            : null;

                    if (!dateA || !dateA.isValid) return b;
                    if (!dateB || !dateB.isValid) return a;

                    return dateB.isBefore(dateA.get()) ? a : b;
                });

                setLastJob(latestJob);
            }

            if (
                jobs.pendingConfirmationJobs.length ||
                jobs.productionJobs.length ||
                jobs.quotedJobs.length ||
                jobs.pendingApprovalJobs.length ||
                jobs.completedJobs.length ||
                jobs.pendingPaymentJobs.length
            ) {
                setIsNewCustomer(false);
            }
        }
    }, [jobs]);

    // Polling every minute when resetPriceStatus or priceExpire is true
    useEffect(() => {
        if (!jobs?.quotedJobs?.length) return;

        const hasResetOrExpired = jobs.quotedJobs.some(
            (job) => job.resetPriceStatus
        );

        if (!hasResetOrExpired) {
            setPoll(false);
            return;
        }

        setPoll(true);
    }, [jobs]);

    return {
        loader: isUninitialized || isLoading,
        userName: userProfile.hasOwnProperty('name') ? userProfile.name : '',
        jobs,
        isNewCustomer,
        dialog,
    };
};
